import React from 'react';
import styled, { createGlobalStyle } from "styled-components";
import Button, { ButtonProps } from "@material-ui/core/Button";
import TextField, { TextFieldProps } from "@material-ui/core/TextField";

export const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

export const Flex = styled.div<{
  direction?: "row" | "column";
  justify?:
  | "flex-start"
  | "flex-end"
  | "center"
  | "space-between"
  | "space-around"
  | "space-evenly";
  alignItems?: "flex-start" | "flex-end" | "center" | "stretch" | "baseline";
  wrap?: "nowrap" | "wrap" | "wrap-reverse";
  gap?: string;
}>`
  display: flex;
  flex-direction: ${(props) => props.direction || "row"};
  justify-content: ${(props) => props.justify || "flex-start"};
  align-items: ${(props) => props.alignItems || "stretch"};
  flex-wrap: ${(props) => props.wrap || "nowrap"};
  gap: ${(props) => props.gap || "0"};
`;

export const CloseButton = styled(Button) <ButtonProps>`
  min-width: auto;
  padding: 6px;
` as typeof Button;

export const DeleteIconButton = styled(Button) <ButtonProps>`
  min-width: auto;
  padding: 8px;
  border-radius: 50%;
  &:hover {
    background-color: rgba(255, 0, 0, 0.1);
  }
` as typeof Button;

interface PromptButtonProps extends ButtonProps {
  tint?: string;
  textcolor?: string;
}

const StyledPromptButton = styled(Button) <PromptButtonProps>`
  background: ${({ tint }) => tint} !important;
  color: ${({ textcolor }) => textcolor} !important;
  margin: 0 8px !important;
`;

export const PromptButton = React.forwardRef<HTMLButtonElement, PromptButtonProps>(
  ({ textcolor, tint, ...rest }, ref) => {
    return <StyledPromptButton ref={ref} textcolor={textcolor} tint={tint} {...rest} />;
  }
);

export const PromptHeader = styled.h3`
  font-size: 30px;
  margin-bottom: 20px;
`;

export const PromptBody = styled.span`
  font-size: 16px;
  margin-bottom: 20px;
`;

export const Prompt = styled(Flex)`
  height: 400px;
  width: 600px;
  font-family: "Roboto", sans-serif;
  text-align: center;
`;

export const InputItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 32px;
  width: 100%;
`;

export const Tag = styled.div`
  font-weight: 800;
  margin-right: 32px;
  min-width: 200px;
  width: 200px;
  text-align: left;
  flex-shrink: 0;
  font-size: 16px;
  color: #1a1a1a;
`;

export const HelpText = styled.div`
  margin-left: 12px;
  font-style: italic;
`;

export const PrimaryHeader = styled.h1`
  font-size: 32px;
  font-weight: 600;
`;

export const SecondaryHeader = styled.h2`
  font-size: 24px;
  font-weight: 300;
`;

export const Container = styled.div`
  padding: 28px;
`;

interface InputWidthProps {
  $setWidth?: string | number;
}

export const StyledInput = styled((props: InputWidthProps & TextFieldProps) => <TextField {...props} />)`
  width: ${(props) => {
    if (!props.$setWidth) return "100%";
    return typeof props.$setWidth === "number"
      ? `${props.$setWidth}px`
      : props.$setWidth;
  }};
` as React.ComponentType<InputWidthProps & TextFieldProps>;

export const StyledNumberInput = styled((props: InputWidthProps & TextFieldProps) => <TextField {...props} />)`
  width: ${(props) => {
    if (!props.$setWidth) return "auto";
    return typeof props.$setWidth === "number"
      ? `${props.$setWidth}px`
      : props.$setWidth;
  }};
` as React.ComponentType<InputWidthProps & TextFieldProps>;

export const SectionWrapper = styled.div`
  margin-bottom: 40px;
`;

export const FormCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

export const FieldGroup = styled.div`
  margin-bottom: 24px;
  &:last-child {
    margin-bottom: 0;
  }
`;

export const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #4a5568;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.025em;
`;

export const HelpTextInline = styled.span`
  display: block;
  font-size: 12px;
  color: #a0aec0;
  margin-top: 6px;
  font-weight: 500;
`;

export const ActionToolbar = styled(Flex)`
  margin-top: 32px;
  gap: 12px;
  border-top: 1px solid #f1f5f9;
  padding-top: 24px;
`;

export const ToolbarButton = styled(Button)`
  && {
    height: 48px;
    padding: 0 32px;
    font-weight: 700;
    text-transform: none;
    border-radius: 10px;
    font-size: 15px;
    &.MuiButton-containedPrimary {
      box-shadow: 0 4px 12px rgba(63, 81, 181, 0.2);
    }
  }
`;

export const NavBackButton = styled(Button)`
  && {
    color: #64748b;
    font-weight: 600;
    text-transform: none;
    font-size: 15px;
    padding: 8px 16px 8px 12px;
    border-radius: 8px;
    justify-content: flex-start;
    transition: all 0.2s ease;
    margin-bottom: 24px;
    
    &:hover {
      background-color: #f1f5f9;
      color: #0f172a;
      transform: translateX(-4px);
    }
    
    .MuiButton-startIcon {
      margin-right: 8px;
    }
  }
`;

export const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

